import {
  interfaceUrlAndNameAtomFamily_SUSPENDS,
  nodeAtomFamily,
  NodeType,
  AssetType,
  previewNodeIdAtom,
  isDefaultNode,
  assetAtomFamily_SUSPENDS,
  determineAssetType,
  getAssetId,
  getTablesOrigin,
  DefaultNodeData as NewDefaultNodeData,
  getAgentsOrigin,
  DefaultNodeData,
  getAssetTitleAtom,
} from "@zapier/canvas-state";
import { useAtomValue, useSetAtom } from "jotai";
import { Suspense, useEffect } from "react";

import { FocusModeView } from "./FocusModeView";

function getUrl(assetType: AssetType, assetId: string) {
  if (assetType === "zap") {
    return `/editor/${assetId}/draft`;
  }
  if (assetType === "table") {
    return `https://${getTablesOrigin()}/app/tables/t/${assetId}`;
  }

  if (assetType === "chatbot") {
    return `/app/chatbots/${assetId}`;
  }

  if (assetType === "agent") {
    return `${getAgentsOrigin()}/bots/${assetId}/settings-preview`;
  }
}

function SuspendedComponent({ previewNodeId }: { previewNodeId: string }) {
  const getAssetTitle = useAtomValue(getAssetTitleAtom);
  const setPreviewNodeIdAtom = useSetAtom(previewNodeIdAtom);
  const node = useAtomValue(nodeAtomFamily(previewNodeId));
  const assetType = determineAssetType(
    (node.data as DefaultNodeData).services,
    node.type as NodeType,
  );
  const selectedAssetId = getAssetId(node.data, assetType);
  const asset = useAtomValue(
    assetAtomFamily_SUSPENDS({
      assetType,
      assetId: selectedAssetId,
      interfaceProjectId: (node.data as NewDefaultNodeData).projectId,
    }),
  );
  const url = getUrl(assetType, selectedAssetId);

  // Handle escape key to close component
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPreviewNodeIdAtom(null);
      }
    };
    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [setPreviewNodeIdAtom]);

  if (
    assetType === "interface" &&
    isDefaultNode(node) &&
    node.data.projectId &&
    node.data.pageId
  ) {
    return (
      <FocusModeInterfaceWrapper
        interfaceProjectId={node.data.projectId}
        interfacePageId={node.data.pageId}
        previewNodeId={previewNodeId}
      />
    );
  }

  return (
    <FocusModeView
      title={getAssetTitle({ asset, assetType })}
      url={url}
      assetType={assetType}
      assetId={selectedAssetId}
      onClose={() => {
        setPreviewNodeIdAtom(null);
      }}
    />
  );
}

export function FocusModeWrapper({ previewNodeId }: { previewNodeId: string }) {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent previewNodeId={previewNodeId} />
    </Suspense>
  );
}

function FocusModeInterfaceWrapper({
  interfaceProjectId,
  interfacePageId,
  previewNodeId,
}: {
  interfaceProjectId: string;
  interfacePageId: string;
  previewNodeId: string;
}) {
  const getAssetTitle = useAtomValue(getAssetTitleAtom);
  const setPreviewNodeId = useSetAtom(previewNodeIdAtom);

  const { url: interfaceUrl, projectName } = useAtomValue(
    interfaceUrlAndNameAtomFamily_SUSPENDS({
      projectId: interfaceProjectId,
      pageId: interfacePageId,
      nodeId: previewNodeId,
    }),
  );

  const handleClose = () => {
    setPreviewNodeId(null);
  };

  return (
    <FocusModeView
      title={getAssetTitle({ asset: projectName, assetType: "interface" })}
      url={interfaceUrl || undefined}
      assetType="interface"
      assetId={interfacePageId}
      onClose={handleClose}
    />
  );
}
